import React, { useState } from 'react';
import { database } from '../../../../firebase';
import { ref, push, set } from 'firebase/database';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD || "http://localhost:8000";

const ManagerRequestModal = ({ employeeId, companyUID, requestType, onClose, onSuccess, employeeName, managerId, managerName }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    reason: '',
    date: '',
    message: ''
  });

  const getTitle = () => {
    switch (requestType) {
      case 'salary': return 'Request Salary Change';
      case 'bonus': return 'Request Bonus';
      case 'offboarding': return 'Initiate Offboarding';
      case 'info': return 'Request Information';
      default: return 'Request';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!companyUID || !employeeId) return;
    setLoading(true);

    try {
      if (requestType === 'info') {
        // Direct notification to employee
        const notifRef = ref(database, `companies/${companyUID}/employees/${employeeId}/notifications`);
        const newNotifRef = push(notifRef);
        await set(newNotifRef, {
          title: 'Information Requested',
          message: `${managerName} has requested information: "${formData.message}"`,
          type: 'info',
          timestamp: Date.now(),
          read: false,
          senderId: managerId,
          senderName: managerName
        });
        
        // Try to send email (fire and forget)
        try {
          fetch(`${BACKEND_URL}/send-notification-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              companyId: companyUID,
              employeeId: employeeId,
              subject: 'Information Requested',
              message: `${managerName} has requested information: "${formData.message}"`,
              type: 'info'
            })
          }).catch(err => console.log('Email send failed:', err));
        } catch (e) {
          console.log('Email trigger error', e);
        }

      } else {
        // Request needing HR approval
        const reqRef = ref(database, `companies/${companyUID}/employees/${employeeId}/requests`);
        const newReqRef = push(reqRef);
        
        const payload = {
          type: requestType,
          status: 'pending_hr',
          requestedBy: managerId,
          requestedByName: managerName,
          requestedAt: Date.now(),
          ...formData
        };

        // Clean up empty fields
        if (!payload.amount) delete payload.amount;
        if (!payload.date) delete payload.date;
        if (!payload.message) delete payload.message;

        await set(newReqRef, payload);

        // Notify Admin + All Collaborators about this pending request
        const requestTypeLabels = {
          salary: 'Salary Change',
          bonus: 'Bonus',
          offboarding: 'Offboarding'
        };
        const requestLabel = requestTypeLabels[requestType] || requestType;
        
        try {
          await fetch(`${BACKEND_URL}/notify-admin-and-collaborators`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              companyId: companyUID,
              subject: `New ${requestLabel} Request Pending Review`,
              message: `${managerName} has submitted a ${requestLabel.toLowerCase()} request for ${employeeName} that requires HR approval. Please review this request in the My Team section.`,
              type: 'info',
              triggeredBy: managerName,
              employeeName: employeeName,
              requestType: requestType
            })
          });
        } catch (e) {
          console.log('Admin notification failed (non-critical):', e);
        }
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error submitting request:', error);
      alert('Failed to submit request.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3000,
    }} onClick={onClose}>
      <div style={{
        background: '#0B1C34',
        border: '1px solid rgba(86, 141, 237, 0.2)',
        borderRadius: '12px',
        width: '500px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'auto',
        color: '#FFFFFF',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px',
          borderBottom: '1px solid rgba(86, 141, 237, 0.15)',
        }}>
          <h2 style={{
            margin: 0,
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '20px',
            fontWeight: 600,
          }}>{getTitle()}</h2>
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.7)',
              fontSize: '24px',
              cursor: 'pointer',
              padding: 0,
              lineHeight: 1,
            }}
          >&times;</button>
        </div>
        
        {/* Body */}
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          <p style={{ marginBottom: 20, color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
            {requestType === 'info' 
              ? `Send a message to ${employeeName} requesting specific information.`
              : `Submit a request for HR approval regarding ${employeeName}.`
            }
          </p>

          {(requestType === 'salary' || requestType === 'bonus') && (
            <div style={{ marginBottom: 16 }}>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontSize: '14px',
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.9)',
              }}>Amount</label>
              <input 
                type="number" 
                placeholder="0.00" 
                value={formData.amount}
                onChange={e => setFormData({...formData, amount: e.target.value})}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#FFFFFF',
                  fontSize: '16px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          )}

          {requestType === 'offboarding' && (
            <div style={{ marginBottom: 16 }}>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontSize: '14px',
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.9)',
              }}>Proposed Last Day</label>
              <input 
                type="date" 
                value={formData.date}
                onChange={e => setFormData({...formData, date: e.target.value})}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#FFFFFF',
                  fontSize: '16px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          )}

          {requestType === 'info' ? (
            <div style={{ marginBottom: 16 }}>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontSize: '14px',
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.9)',
              }}>Message</label>
              <textarea 
                placeholder="What information do you need?"
                value={formData.message}
                onChange={e => setFormData({...formData, message: e.target.value})}
                required
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#FFFFFF',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                }}
              />
            </div>
          ) : (
            <div style={{ marginBottom: 16 }}>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontSize: '14px',
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.9)',
              }}>Reason / Justification</label>
              <textarea 
                placeholder="Why are you making this request?"
                value={formData.reason}
                onChange={e => setFormData({...formData, reason: e.target.value})}
                required
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#FFFFFF',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                }}
              />
            </div>
          )}

          {/* Footer */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '24px',
            paddingTop: '20px',
            borderTop: '1px solid rgba(86, 141, 237, 0.15)',
          }}>
            <button 
              type="button" 
              onClick={onClose} 
              disabled={loading}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.5 : 1,
              }}
            >Cancel</button>
            <button 
              type="submit" 
              disabled={loading}
              style={{
                padding: '10px 20px',
                background: '#568ded',
                border: 'none',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManagerRequestModal;

